export type AnnotationSource = { id: number; text: string; annotations: string };
export type AnnotationSelection = AnnotationSource & { offset: number; number: number; body: string | null; sourceNumber: number | null };

export const isAnnotationMarker = (value: string) => /^[⑴-⒇]$/.test(value);

export function annotationEntries(raw: string) {
  // Some imported books put every note in one paragraph; line breaks are not boundaries.
  const matches = [...raw.matchAll(/(?:【注[释釋]】|注[释釋][：:]?)?\s*([⑴-⒇](?:[、，\s]*[⑴-⒇])*)/g)];
  const entries = matches.map((match, index) => ({
    numbers: [...match[1].matchAll(/[⑴-⒇]/g)].map(m => m[0].charCodeAt(0) - 0x2473),
    body: raw.slice(match.index! + match[0].length, matches[index + 1]?.index ?? raw.length).trim(),
  }));
  const unnumbered = raw.slice(0, matches[0]?.index ?? raw.length).replace(/^(?:【注[释釋]】|注[释釋][：:]?)\s*/, '').trim();
  if (unnumbered) entries.unshift({ numbers: [], body: unnumbered });
  return entries;
}

/** Resolve source labels/occurrences only; never renumber or rewrite the edition. */
function noteAt(source: AnnotationSource, offset: number) {
  const number = source.text.charCodeAt(offset) - 0x2473;
  const entries = annotationEntries(source.annotations);
  const matches = entries.filter(entry => entry.numbers.includes(number));
  if (matches.length === 1) return matches[0];
  const before = source.text.slice(0, offset).replace(/[⑴-⒇\s，。；：、“”‘’！？]/g, '');
  const named = entries.filter(entry => {
    const lemma = entry.body.match(/^([^—：:\n]{1,30})(?:——|—|：|:)/)?.[1];
    return lemma?.split(/[，、]/).some(word => word.trim() && before.endsWith(word.replace(/[\s“”‘’]/g, '')));
  });
  if (named.length === 1) return named[0];
  if (entries.length === 1 && entries[0].numbers.length === 0) return entries[0];
  const occurrences = [...source.text.matchAll(new RegExp(source.text[offset], 'g'))];
  if (matches.length > 1 && matches.length === occurrences.length) return matches[occurrences.findIndex(match => match.index === offset)];
  return null;
}

/** An occurrence in exact source bytes, never a chapter-wide note-number key. */
export function selectAnnotation(source: AnnotationSource, offset: number): AnnotationSelection | null {
  if (!Number.isInteger(offset) || offset < 0 || !isAnnotationMarker(source.text[offset] ?? "")) return null;
  const entry = noteAt(source, offset);
  return { id: source.id, text: source.text, annotations: source.annotations, offset,
    number: source.text.charCodeAt(offset) - 0x2473, body: entry?.body ?? null, sourceNumber: entry?.numbers[0] ?? null };
}

export function currentAnnotation(source: AnnotationSource, selection: AnnotationSelection | null) {
  if (!selection || source.id !== selection.id || source.text !== selection.text || source.annotations !== selection.annotations) return null;
  return selection;
}
